import React from "react";
import { connect } from "react-redux";
import "./SavingContainer.css";
import { GetRiseSavingGraphDetails } from "actions/kpiActions";
import { resetError, defineScopeLoading, pendingChanges } from "actions/spendActions";
import Popup from "reactjs-popup";
import { formatThousands } from 'utils/Utils'

// utility: parse numeric from user input (removes commas, currency symbols)
const parseNumeric = (val) => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;
  let s = String(val).trim();
  if (!s) return 0;
  s = s.replace(/[$,\s\u00A0]/g, ""); // remove all currency symbols dynamically
  if (/^\(.+\)$/.test(s)) s = "-" + s.replace(/[()]/g, "");
  const n = Number(s);
  return isNaN(n) ? 0 : n;
};

const formatCurrency = (num, currency = "$", decimals = 0) => {
  if (num === null || num === undefined || num === "") return "";
  const n = typeof num === "number" ? num : parseNumeric(num);
  return currency + n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatPercent = (val, decimals = 2) => {
  const n = typeof val === "number" ? val : parseNumeric(val);
  return (isNaN(n) ? 0 : n).toFixed(decimals) + " %";
};

class S4CostPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      cost: 0,
      costInput: "0",
      sbnSavings: 0,
      percentFunding: 0,
      error: "",
      isSaveInProgress: false,
      hasChanges: false,
      responseMsg: "",
      loading: true,
      currency: "$",
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData() {
    const dmcId = this.props.spendDetails.currentActiveRequest.DMC_ID;
    const vsId = this.props.spendDetails.currentActiveRequest.VS_ID;
    const user = this.props.user.user.Email;

    this.setState({ loading: true });

    this.props
      .GetRiseSavingGraphDetails(dmcId, vsId, user, 0, "R")
      .then((res) => {
        this.setState({ loading: false });
        if (res && res.data.Result.Table1 && res.data.Result.Table1.length > 0) {
          const row = res.data.Result.Table1[0];
          const costValue = parseNumeric(row.COST_S4_PROJECT);
          const sbnValue = row.CAPTR_SBN_ADOPTION_SAVING;
          const pctValue = parseNumeric(row.PER_FUNDING_CALC.replace("%", ""));
          this.setState({
            cost: costValue,
            costInput: costValue.toLocaleString("en-US", { minimumFractionDigits: 0 }),
            sbnSavings: sbnValue,
            percentFunding: pctValue,
            currency: row.CURRENCY_SYMBOL || "$"
          });
        }
      })
      .catch((err) => {
        this.setState({ loading: false, error: "Failed to load data" });
      });
  }

  handleCostChange = (e) => {
    const raw = e.target.value || "";
    const candidate = raw.replace(/[^\d\-\.\(\),]/g, "");
    const numeric = parseNumeric(candidate);

    // Only allow non-negative
    if (numeric < 0) return;

    this.props.pendingChanges(true);
    this.setState({
      costInput: candidate,
      cost: numeric,
      hasChanges: true,
      responseMsg: "Pending changes. Click Save to update.",
      error: "",
    });
  };

  handleCostBlur = () => {
    const num = this.state.cost;
    if (num === null || num === undefined || num === 0) {
      this.setState({ costInput: "", error: "", hasChanges: true });
      return;
    }
    // Non-negative validation only
    if (num < 0) {
      this.setState({
        error: "Cost cannot be negative.",
      });
    } else {
      this.setState({ error: "" });
    }

    this.setState({
      costInput: num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
    });
  };

  onSave = async () => {
    if (this.state.isSaveInProgress) return;

    const dmcId = this.props.spendDetails.currentActiveRequest.DMC_ID;
    const vsId = this.props.spendDetails.currentActiveRequest.VS_ID;
    const user = this.props.user.user.Email;

    const { cost } = this.state;
    if (cost < 0) {
      this.setState({ error: "Cost cannot be negative." });
      return;
    }

    this.setState({ isSaveInProgress: true, responseMsg: "", error: "" });

    try {
      if (this.props.GetRiseSavingGraphDetails && typeof this.props.GetRiseSavingGraphDetails === "function") {
        await this.props.GetRiseSavingGraphDetails(
          dmcId,
          vsId,
          user,
          this.state.cost,
          "W"
        );
      }

      this.setState({ hasChanges: false, responseMsg: "Saved successfully" });
      this.props.pendingChanges(null);
      this.loadData();
    } catch (err) {
      this.setState({ error: (err && err.message) || "Save failed", responseMsg: "" });
    } finally {
      this.setState({ isSaveInProgress: false });
    }
  };

  render() {
    const { costInput, sbnSavings, percentFunding, error, isSaveInProgress, hasChanges, responseMsg, currency } = this.state;
    
    const raw = String(sbnSavings || "");
    const firstChar = raw.charAt(0);
    const numericOnly = raw.replace(/[^0-9.-]/g, "");            // "123456" or "123456.00"
    const formattedNumber = formatThousands(Number(numericOnly)); // e.g. "123,456.00"
    const sbnDisplay = `${firstChar}${formattedNumber}`;            // e

    return (
      <div className="saving-table s4-cost-panel" style={{ maxWidth: 420 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td style={{ padding: 8, verticalAlign: "middle" }}>
                <div style={{ fontSize: 16, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                  Cost of S/4 Project                  
                  <span style={{ float: 'right' }}>
												<Popup
													className="report-tooltip"
													trigger={
														<img
															style={{ marginLeft: '5px', paddingBottom: '0px' }}
															className="info-icon"
															src="images/info-icon.svg"
															width="15"
														/>
													}
													position="bottom left"
													on="hover"
													contentStyle={{
														maxWidth: '300px',
														whiteSpace: 'normal',
														wordWrap: 'break-word',
														overflowWrap: 'break-word',
														padding: '10px',
														zIndex: 1000,
													}}
												>
													<div>
                            Estimated pricing for a S/4 Project is $4M to $6M
													</div>
												</Popup>
											</span>
                </div>
              </td>
              <td style={{ padding: 8, textAlign: "right", width: 200 }}>
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                  <span style={{ marginRight: 6 }}>{currency}</span>
                  <input
                    type="text"
                    value={costInput}
                    onChange={this.handleCostChange}
                    onBlur={this.handleCostBlur}
                    style={{
                      width: 160,
                      padding: "6px 8px",
                      textAlign: "right",
                      border: "1px solid #ccc",
                      borderRadius: 4,
                    }}
                    aria-label="Cost of S/4 Project"
                  />
                </div>
              </td>
            </tr>

            {/* <tr>
              <td style={{ padding: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>SBN Adoption Savings</div>
              </td>
              <td style={{ padding: 8, textAlign: "right", width: 200 }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>{formatCurrency(sbnSavings, currency, 0)}</div>
              </td>
            </tr> */}
            <tr>
              <td style={{ padding: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Capturable SBN Adoption Savings</div>
              </td>
              <td style={{ padding: 8, textAlign: "right", width: 200 }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>{sbnDisplay}</div>
              </td>
            </tr>           

            <tr>
              <td style={{ padding: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 600 }}>% of Funding</div>
              </td>
              <td style={{ padding: 8, textAlign: "right", width: 200 }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>{formatPercent(percentFunding, 2)}</div>
              </td>
            </tr>

            <tr>
              <td colSpan={2} style={{ padding: 8 }}>
                {error && <div style={{ color: "red" }}>{error}</div>}
                {!error && responseMsg && <div style={{ color: "green" }}>{responseMsg}</div>}
              </td>
            </tr>

            <tr>
              <td colSpan={2} style={{ padding: 8, textAlign: "right" }}>
                <button
                  type="button"
                  onClick={this.onSave}
                  disabled={!hasChanges || isSaveInProgress}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 4,
                    border: "none",
                    cursor: hasChanges && !isSaveInProgress ? "pointer" : "not-allowed",
                    background: hasChanges && !isSaveInProgress ? "#0077cc" : "#ddd",
                    color: hasChanges && !isSaveInProgress ? "#fff" : "#888",
                  }}
                >
                  {isSaveInProgress ? "Saving..." : "Save"}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    ui: state.content.ui,
    userData: state.valueStory,
    user: state.default,
    spendDetails: state.spendDetails
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    resetError: (error) => dispatch(resetError(error)),
    defineScopeLoading: (bool) => dispatch(defineScopeLoading(bool)),
    GetRiseSavingGraphDetails: (DMCId, VSId, userEmail, COST_S4_PROJ, REC_FLAG) =>
      dispatch(GetRiseSavingGraphDetails(DMCId, VSId, userEmail, COST_S4_PROJ, REC_FLAG)),
    pendingChanges: (changes) => dispatch(pendingChanges(changes)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(S4CostPanel);
